import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { ThreeMFLoader } from "three/examples/jsm/loaders/3MFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { unzipSync, strFromU8 } from "three/examples/jsm/libs/fflate.module.js";
import { MeshBVH, ExtendedTriangle } from "three-mesh-bvh";
import { PublicNav, PublicFooter } from "@/components/public/PublicChrome";
import { useShop } from "@/components/public/Shop";
import { LoginGate, GUEST_MAX_MB } from "@/components/public/LoginGate";
import { useNavigate } from "react-router-dom";
import { calcTax, taxLabel } from "@/lib/tax";
import { api, formatIDR, API_BASE } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, RotateCw, Grid3x3, Box, Loader2, Send, MessageCircle, Plus, Trash2, XCircle, AlertTriangle, CheckCircle2, Info, ChevronDown, Zap, Palette, Scissors, Lock, Unlock, Focus, LogIn, LogOut, UserCog, Pencil, Eye, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BUILD_VOLUME = { x: 256, y: 256, z: 256 }; // reference FDM build volume (mm)

// ETA helpers — REGULER: hari kerja (Sabtu–Minggu libur); EXPRESS: waktu berjalan (kalender)
const fmtEta = (d) => d.toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short" });
const etaWorkDays = (n) => { const d = new Date(); let a = 0; while (a < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) a++; } return d; };
const etaCalDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d; };

const waNumber = (s) => { let d = String(s || "").replace(/\D/g, ""); if (d.startsWith("0")) d = "62" + d.slice(1); else if (!d.startsWith("62")) d = "62" + d; return d; };
export const greenMat = () => new THREE.MeshStandardMaterial({ color: 0x2a7d66, metalness: 0.1, roughness: 0.6 });
const FDM_COLORS = [
  { name: "Putih", hex: "#FFFFFF" }, { name: "Hitam", hex: "#1A1A1A" }, { name: "Abu", hex: "#9CA3AF" },
  { name: "Merah", hex: "#DC2626" }, { name: "Oranye", hex: "#EA580C" }, { name: "Kuning", hex: "#FACC15" },
  { name: "Hijau", hex: "#16A34A" }, { name: "Biru", hex: "#2563EB" }, { name: "Navy", hex: "#1E3A8A" },
  { name: "Ungu", hex: "#7C3AED" }, { name: "Pink", hex: "#EC4899" }, { name: "Emas", hex: "#D4AF37" },
];
const SLA_COLORS = [{ name: "Putih", hex: "#FFFFFF" }, { name: "Hitam", hex: "#1A1A1A" }, { name: "Clear", hex: "#DCE7F5" }];

function analyzeGeometry(geom) {
  geom.computeBoundingBox();
  const size = new THREE.Vector3();
  geom.boundingBox.getSize(size);
  const pos = geom.attributes.position;
  const index = geom.index;
  const triCount = index ? index.count / 3 : pos.count / 3;
  const gi = (k) => (index ? index.getX(k) : k);
  let vol = 0, area = 0;
  for (let t = 0; t < triCount; t++) {
    const i0 = gi(t * 3), i1 = gi(t * 3 + 1), i2 = gi(t * 3 + 2);
    const ax = pos.getX(i0), ay = pos.getY(i0), az = pos.getZ(i0);
    const bx = pos.getX(i1), by = pos.getY(i1), bz = pos.getZ(i1);
    const cx = pos.getX(i2), cy = pos.getY(i2), cz = pos.getZ(i2);
    vol += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6;
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const wx = cx - ax, wy = cy - ay, wz = cz - az;
    const nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
    area += 0.5 * Math.sqrt(nx * nx + ny * ny + nz * nz);
  }
  return { volume_cm3: Math.abs(vol) / 1000, surface_cm2: area / 100, bbox: [size.x, size.y, size.z], triangles: triCount };
}

// analyze ANY object (STL mesh / OBJ group / 3MF group): sum volume+triangles, union world bbox, count colors
export function analyzeObject(content) {
  content.updateMatrixWorld(true);
  let vol = 0, surf = 0, tris = 0; const box = new THREE.Box3(); const colors = new Set();
  content.traverse((c) => {
    if (c.isMesh && c.geometry?.attributes?.position) {
      if (!c.geometry.attributes.normal) c.geometry.computeVertexNormals();
      const a = analyzeGeometry(c.geometry);
      const sc = new THREE.Vector3(); c.getWorldScale(sc);
      vol += a.volume_cm3 * Math.abs((sc.x * sc.y * sc.z) || 1);
      const areaScale = (Math.abs(sc.x * sc.y) + Math.abs(sc.y * sc.z) + Math.abs(sc.x * sc.z)) / 3 || 1;
      surf += a.surface_cm2 * areaScale;
      tris += a.triangles;
      c.geometry.computeBoundingBox();
      box.union(c.geometry.boundingBox.clone().applyMatrix4(c.matrixWorld));
      (Array.isArray(c.material) ? c.material : [c.material]).forEach((m) => { if (m?.color) colors.add(m.color.getHexString()); });
    }
  });
  const size = new THREE.Vector3(); box.getSize(size);
  return { volume_cm3: vol, surface_cm2: surf, bbox: [size.x || 1, size.y || 1, size.z || 1], triangles: tris, colorCount: colors.size || 1 };
}

// auto-orient: letakkan permukaan datar terbesar menempel ke bed (down = -Y), lalu center + drop to bed
export function autoOrient(content, triCap = 250000) {
  content.updateMatrixWorld(true);
  const buckets = new Map();
  let totalTris = 0;
  content.traverse((c) => { if (c.isMesh && c.geometry?.attributes?.position) { const g = c.geometry; totalTris += g.index ? g.index.count / 3 : g.attributes.position.count / 3; } });
  if (totalTris === 0 || totalTris > triCap) return; // lewati file sangat besar demi performa
  const a = new THREE.Vector3(), b = new THREE.Vector3(), cc = new THREE.Vector3();
  const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), cr = new THREE.Vector3();
  content.traverse((c) => {
    if (!(c.isMesh && c.geometry?.attributes?.position)) return;
    const g = c.geometry; const pos = g.attributes.position; const idx = g.index; const nm = c.matrixWorld;
    const n = idx ? idx.count / 3 : pos.count / 3; const gi = (k) => (idx ? idx.getX(k) : k);
    for (let t = 0; t < n; t++) {
      a.fromBufferAttribute(pos, gi(t * 3)).applyMatrix4(nm);
      b.fromBufferAttribute(pos, gi(t * 3 + 1)).applyMatrix4(nm);
      cc.fromBufferAttribute(pos, gi(t * 3 + 2)).applyMatrix4(nm);
      e1.subVectors(b, a); e2.subVectors(cc, a); cr.crossVectors(e1, e2);
      const area = cr.length() / 2; if (area <= 1e-9) continue;
      cr.normalize();
      const key = `${Math.round(cr.x * 12)}_${Math.round(cr.y * 12)}_${Math.round(cr.z * 12)}`;
      const cur = buckets.get(key) || { area: 0, dir: new THREE.Vector3() };
      cur.area += area; cur.dir.addScaledVector(cr, area); buckets.set(key, cur);
    }
  });
  let best = null, bestArea = 0;
  buckets.forEach((v) => { if (v.area > bestArea) { bestArea = v.area; best = v.dir.clone().normalize(); } });
  if (best && best.lengthSq() > 0) {
    const q = new THREE.Quaternion().setFromUnitVectors(best, new THREE.Vector3(0, -1, 0));
    content.applyQuaternion(q); content.updateMatrixWorld(true);
  }
}

// center on X/Z + drop to bed (flat-to-bed) inside a pivot group
function wrapBed(content) {
  const box = new THREE.Box3().setFromObject(content);
  const ctr = new THREE.Vector3(); box.getCenter(ctr);
  content.position.set(-ctr.x, -box.min.y, -ctr.z);
  const pivot = new THREE.Group(); pivot.add(content); return pivot;
}

// deteksi self-intersection (parameter tabel SISTEM FOMU) via BVH — segitiga tetangga (berbagi verteks) dikecualikan,
// dan hanya irisan DALAM shell/komponen yang sama (irisan antar-shell berbeda = multi-body, aman di slicer)
export function selfIntersections(geom, triRoots = null, triCap = 60000) {
  const pos = geom.attributes.position; const index = geom.index;
  const triCount = index ? index.count / 3 : pos.count / 3;
  if (!triCount || triCount > triCap) return null; // file sangat besar: dilewati demi performa
  try {
    const bvh = new MeshBVH(geom);
    const gi = (k) => (index ? index.getX(k) : k);
    const tri = new ExtendedTriangle();
    const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3();
    const hit = new Set();
    const eps2 = 1e-4; // 0.01 mm — toleransi kuantisasi float32 STL pada model ratusan mm
    for (let t = 0; t < triCount; t++) {
      if (hit.has(t)) continue;
      va.fromBufferAttribute(pos, gi(t * 3)); vb.fromBufferAttribute(pos, gi(t * 3 + 1)); vc.fromBufferAttribute(pos, gi(t * 3 + 2));
      tri.a.copy(va); tri.b.copy(vb); tri.c.copy(vc); tri.needsUpdate = true;
      bvh.shapecast({
        intersectsBounds: (box) => box.intersectsTriangle(tri),
        intersectsTriangle: (t2, t2i) => {
          if (t2i <= t || hit.has(t2i)) return false;
          if (triRoots && triRoots[t2i] !== triRoots[t]) return false; // beda shell/komponen = multi-body, bukan kerusakan
          let shared = 0;
          for (const p1 of [va, vb, vc]) {
            for (const p2 of [t2.a, t2.b, t2.c]) {
              if (p1.distanceToSquared(p2) < eps2) { shared++; break; }
            }
          }
          if (shared >= 1) return false; // berbagi verteks = kontak titik/edge yang sah, bukan self-intersection
          hit.add(t); hit.add(t2i);
          return false;
        },
      });
    }
    return hit.size;
  } catch { return null; }
}

export function meshIntegrity(geom, triCap = 160000) {
  const pos = geom.attributes.position;
  const index = geom.index;
  const triCount = index ? index.count / 3 : pos.count / 3;
  if (triCount > triCap) return null;
  const gi = (k) => (index ? index.getX(k) : k);
  const key = (i) => `${Math.round(pos.getX(i) * 1000)}_${Math.round(pos.getY(i) * 1000)}_${Math.round(pos.getZ(i) * 1000)}`;
  const edges = new Map();
  const parent = new Map();
  const find = (x) => { let r = x; while (parent.get(r) !== r) { parent.set(r, parent.get(parent.get(r))); r = parent.get(r); } return r; };
  const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent.set(ra, rb); };
  let degenerate = 0;
  for (let t = 0; t < triCount; t++) {
    const i0 = gi(t * 3), i1 = gi(t * 3 + 1), i2 = gi(t * 3 + 2);
    const a = key(i0), b = key(i1), c = key(i2);
    if (!parent.has(a)) parent.set(a, a);
    if (!parent.has(b)) parent.set(b, b);
    if (!parent.has(c)) parent.set(c, c);
    union(a, b); union(b, c);
    for (const [u, v] of [[a, b], [b, c], [c, a]]) {
      const e = u < v ? u + "|" + v : v + "|" + u;
      edges.set(e, (edges.get(e) || 0) + 1);
    }
    const ax = pos.getX(i0), ay = pos.getY(i0), az = pos.getZ(i0);
    const abx = pos.getX(i1) - ax, aby = pos.getY(i1) - ay, abz = pos.getZ(i1) - az;
    const acx = pos.getX(i2) - ax, acy = pos.getY(i2) - ay, acz = pos.getZ(i2) - az;
    const nx = aby * acz - abz * acy, ny = abz * acx - abx * acz, nz = abx * acy - aby * acx;
    if (nx * nx + ny * ny + nz * nz < 1e-10) degenerate++;
  }
  let boundary = 0, nonmanifold = 0;
  edges.forEach((cnt) => { if (cnt === 1) boundary++; else if (cnt > 2) nonmanifold++; });
  const roots = new Set(); parent.forEach((_, k) => roots.add(find(k)));
  const triRoots = new Array(triCount);
  for (let t = 0; t < triCount; t++) triRoots[t] = find(key(gi(t * 3)));
  const selfint = selfIntersections(geom, triRoots);
  return { boundary, nonmanifold, degenerate, triCount, totalEdges: edges.size, components: roots.size, selfint };
}

// aggregate integrity across every mesh in an object (STL mesh / OBJ / 3MF group)
export function objectIntegrity(content, triCap = 160000) {
  let boundary = 0, nonmanifold = 0, degenerate = 0, triCount = 0, totalEdges = 0, components = 0, ok = false, selfint = 0, siOk = false;
  content.traverse((c) => {
    if (c.isMesh && c.geometry?.attributes?.position) {
      const r = meshIntegrity(c.geometry, triCap);
      if (r) { boundary += r.boundary; nonmanifold += r.nonmanifold; degenerate += r.degenerate; triCount += r.triCount; totalEdges += r.totalEdges; components += r.components; ok = true; if (r.selfint != null) { selfint += r.selfint; siOk = true; } }
    }
  });
  return ok ? { boundary, nonmanifold, degenerate, triCount, totalEdges, components, selfint: siOk ? selfint : null } : null;
}

// Auto repair estimate — TANPA formula. Deteksi kebutuhan repair saja; biaya memakai
// tabel tier flat (Settings → Design & Repair). Kategori awal = tier yang harganya
// paling mendekati harga produksi (volume) file tsb.
export const REPAIR_TIER_DEFAULTS = [["quick", "QUICK", 300000, 3], ["ringan", "RINGAN", 800000, 8], ["medium", "MEDIUM", 1600000, 16], ["berat", "BERAT", 2400000, 24], ["sangat_berat", "SANGAT BERAT", 4800000, 48]];
export function repairTiers(cfg) {
  return REPAIR_TIER_DEFAULTS.map(([key, label, def, defHours]) => ({ key, label, price: (+cfg?.repair_tiers?.[key] > 0) ? +cfg.repair_tiers[key] : def, hours: (+cfg?.repair_hours?.[key] > 0) ? +cfg.repair_hours[key] : defHours }));
}
export const repairPeopleOf = (p) => Math.max(1, Math.min(3, +(p?.repairPeople || 1)));
export const repairEffHoursOf = (p, tier) => (tier ? Math.max(3, tier.hours / repairPeopleOf(p)) : 0);
export function computeRepair(info, integ) {
  const b = integ?.boundary || 0, nm = integ?.nonmanifold || 0, deg = integ?.degenerate || 0;
  const comps = integ?.components || 0;
  const si = integ?.selfint ?? null;
  const empty = (info.volume_cm3 || 0) <= 0.0001;
  // self-intersection TIDAK memicu repair (BVH bisa false-positive pada mesh sehat) — tetap tampil sebagai warning di laporan
  const needed = b > 0 || nm > 0 || deg > 0 || empty;
  return { needed, metrics: { boundary: b, nonmanifold: nm, degenerate: deg, components: comps, selfint: si } };
}

// namespace-safe tag lookup for raw 3MF XML
function tags(el, name) {
  let r = el.getElementsByTagName ? el.getElementsByTagName(name) : [];
  if ((!r || r.length === 0) && el.getElementsByTagNameNS) r = el.getElementsByTagNameNS("*", name);
  return Array.from(r || []);
}
const hexColor = (hex, fallback) => {
  const h = String(hex || "").replace("#", "").slice(0, 6);
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return fallback;
  return new THREE.Color(parseInt(h, 16));
};

// 3MF transform (4x3 row-major, row-vector convention) -> three.js Matrix4 (column-vector)
function mat3mf(str) {
  const v = String(str || "").trim().split(/\s+/).map(Number);
  if (v.length !== 12 || v.some((n) => Number.isNaN(n))) return null;
  return new THREE.Matrix4().set(
    v[0], v[3], v[6], v[9],
    v[1], v[4], v[7], v[10],
    v[2], v[5], v[8], v[11],
    0, 0, 0, 1
  );
}
// read an attribute that may carry a namespace prefix (e.g. p:path)
function attr(el, name) {
  const local = name.includes(":") ? name.split(":")[1] : name;
  return el.getAttribute(name) || (el.getAttributeNS ? el.getAttributeNS("*", local) : null) || el.getAttribute(local);
}

// Robust manual 3MF reader (fallback when ThreeMFLoader can't parse a slicer file).
// Uses REGEX-based streaming (no DOMParser) so very large Bambu/Orca meshes (100MB+ XML)
// don't blow up the browser DOM / memory. Handles inline meshes, multi-material colors
// (per-triangle pindex -> color groups), and the "production extension" where meshes live
// in separate /3D/Objects/*.model files referenced via <component p:path=...>.
const _attrReCache = {};
function xmlAttr(tagText, name) {
  let re = _attrReCache[name];
  if (!re) { re = new RegExp('(?:^|\\s)(?:[\\w.-]+:)?' + name + '="([^"]*)"'); _attrReCache[name] = re; }
  const m = re.exec(tagText);
  return m ? m[1] : null;
}

export function parse3MFManual(arrayBuffer) {
  const files = unzipSync(new Uint8Array(arrayBuffer));
  const norm = (p) => String(p).replace(/^\/+/, "").replace(/\\/g, "/");
  const byPath = {}; for (const p in files) byPath[norm(p)] = files[p];
  const modelPaths = Object.keys(byPath).filter((p) => /\.model$/i.test(p));
  if (!modelPaths.length) throw new Error("3MF: file .model tidak ditemukan di dalam arsip");
  const mainPath = modelPaths.find((p) => /3dmodel\.model$/i.test(p)) || modelPaths[0];
  const greenHex = () => new THREE.Color(0x2a7d66);
  const txtCache = {};
  const textOf = (np) => { if (!(np in txtCache)) txtCache[np] = byPath[np] ? strFromU8(byPath[np]) : null; return txtCache[np]; };

  // Bambu/Orca AMS colors: palette in project_settings.config (filament_colour),
  // per-part extruder in model_settings.config (part id == sub-object id).
  let filamentPalette = [];
  const partExtruder = {};
  const projTxt = textOf(norm("Metadata/project_settings.config"));
  if (projTxt) {
    const m = /"filament_colour"\s*:\s*(\[[^\]]*\])/.exec(projTxt) || /"filament_colors"\s*:\s*(\[[^\]]*\])/.exec(projTxt);
    if (m) { try { filamentPalette = JSON.parse(m[1]); } catch (e) {} }
  }
  const msTxt = textOf(norm("Metadata/model_settings.config"));
  if (msTxt) {
    const pRe = /<part\b([^>]*)>([\s\S]*?)<\/part>/g; let pm;
    while ((pm = pRe.exec(msTxt))) {
      const pid = xmlAttr(pm[1], "id");
      const ex = /key="extruder"\s+value="(\d+)"/.exec(pm[2]);
      if (pid != null && ex) partExtruder[pid] = +ex[1];
    }
  }
  const amsColor = (id) => {
    const e = partExtruder[id];
    return (e && filamentPalette[e - 1]) ? hexColor(filamentPalette[e - 1], null) : null;
  };

  const cache = {};
  const parseModel = (path) => {
    const np = norm(path);
    if (cache[np] !== undefined) return cache[np];
    const text = textOf(np);
    if (!text) return (cache[np] = null);

    // basematerial palettes: groupId -> [displaycolor,...]
    const matGroups = {};
    const bmRe = /<basematerials\b([^>]*)>([\s\S]*?)<\/basematerials>/g; let bm;
    while ((bm = bmRe.exec(text))) {
      const gid = xmlAttr(bm[1], "id"); const cols = [];
      const bRe = /<base\b[^>]*?>/g; let b2;
      while ((b2 = bRe.exec(bm[2]))) cols.push(xmlAttr(b2[0], "displaycolor"));
      if (gid != null) matGroups[gid] = cols;
    }

    const objects = {};
    const objRe = /<object\b([^>]*)>([\s\S]*?)<\/object>/g; let om;
    while ((om = objRe.exec(text))) {
      const head = om[1], body = om[2];
      const id = xmlAttr(head, "id");
      if (id == null) continue;
      const pid = xmlAttr(head, "pid");
      const pindex = +(xmlAttr(head, "pindex") || 0);
      const palette = pid != null ? (matGroups[pid] || []) : [];
      let objColor = palette.length ? hexColor(palette[pindex], greenHex()) : null;
      if (!objColor) objColor = amsColor(id);

      let geom = null, colorSegs = null;
      const vMatch = /<vertices>([\s\S]*?)<\/vertices>/.exec(body);
      const tMatch = /<triangles>([\s\S]*?)<\/triangles>/.exec(body);
      if (vMatch && tMatch) {
        const vTxt = vMatch[1], tTxt = tMatch[1];
        let nv = 0; { const cRe = /<vertex\b/g; while (cRe.exec(vTxt)) nv++; }
        const pos = new Float32Array(nv * 3);
        const vRe = /<vertex\b[^>]*?x="([^"]+)"[^>]*?y="([^"]+)"[^>]*?z="([^"]+)"/g;
        let vi = 0, vv;
        while ((vv = vRe.exec(vTxt))) { pos[vi++] = +vv[1]; pos[vi++] = +vv[2]; pos[vi++] = +vv[3]; }
        let nt = 0; { const cRe = /<triangle\b/g; while (cRe.exec(tTxt)) nt++; }
        const multi = palette.length > 1;
        const tRe = /<triangle\b[^>]*?v1="(\d+)"[^>]*?v2="(\d+)"[^>]*?v3="(\d+)"([^>]*)>/g;
        if (!multi) {
          const idx = new Uint32Array(nt * 3);
          let ti = 0, tt;
          while ((tt = tRe.exec(tTxt))) { idx[ti++] = +tt[1]; idx[ti++] = +tt[2]; idx[ti++] = +tt[3]; }
          geom = new THREE.BufferGeometry();
          geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
          geom.setIndex(new THREE.BufferAttribute(idx, 1));
          geom.computeVertexNormals();
        } else {
          const segs = new Map(); let tt;
          while ((tt = tRe.exec(tTxt))) {
            const ci = +(xmlAttr(tt[4], "p1") ?? pindex);
            let a = segs.get(ci); if (!a) { a = []; segs.set(ci, a); }
            a.push(+tt[1], +tt[2], +tt[3]);
          }
          const positionAttr = new THREE.BufferAttribute(pos, 3);
          colorSegs = [];
          for (const [ci, arr] of segs) {
            const g = new THREE.BufferGeometry();
            g.setAttribute("position", positionAttr);
            g.setIndex(arr);
            g.computeVertexNormals();
            colorSegs.push({ geom: g, color: hexColor(palette[ci], greenHex()) });
          }
        }
      }

      const comps = [];
      const cRe = /<component\b([^>]*?)\/?>/g; let cc;
      while ((cc = cRe.exec(body))) comps.push({ objectid: xmlAttr(cc[1], "objectid"), path: xmlAttr(cc[1], "path"), transform: xmlAttr(cc[1], "transform") });
      objects[id] = { geom, colorSegs, comps, color: objColor };
    }

    const build = [];
    const iRe = /<item\b([^>]*?)\/?>/g; let it;
    while ((it = iRe.exec(text))) build.push({ objectid: xmlAttr(it[1], "objectid"), path: xmlAttr(it[1], "path"), transform: xmlAttr(it[1], "transform") });
    txtCache[np] = null; // free the (potentially huge) text now that geometry is built
    return (cache[np] = { objects, build });
  };

  const mkMat = (color) => new THREE.MeshStandardMaterial({ color: color || greenHex(), metalness: 0.1, roughness: 0.6 });
  const addObjectMeshes = (node, o) => {
    if (o.geom) node.add(new THREE.Mesh(o.geom, mkMat(o.color)));
    if (o.colorSegs) o.colorSegs.forEach((s) => node.add(new THREE.Mesh(s.geom, mkMat(s.color))));
  };
  const buildObject = (path, id, seen) => {
    const key = norm(path) + "#" + id;
    if (seen.has(key)) return null; // guard against cyclic references
    const model = parseModel(path); if (!model) return null;
    const o = model.objects[id]; if (!o) return null;
    const nextSeen = new Set(seen); nextSeen.add(key);
    const node = new THREE.Group();
    addObjectMeshes(node, o);
    o.comps.forEach((c) => {
      const child = buildObject(c.path || path, c.objectid, nextSeen);
      if (child) { const m = mat3mf(c.transform); if (m) child.applyMatrix4(m); node.add(child); }
    });
    return node;
  };

  const main = parseModel(mainPath);
  const root = new THREE.Group();
  const items = (main.build && main.build.length) ? main.build : Object.keys(main.objects).map((id) => ({ objectid: id }));
  items.forEach((it) => {
    const child = buildObject(it.path || mainPath, it.objectid, new Set());
    if (child) { const m = mat3mf(it.transform); if (m) child.applyMatrix4(m); root.add(child); }
  });

  let hasMesh = false; root.traverse((c) => { if (c.isMesh) hasMesh = true; });
  // last resort: render every mesh found in every .model file in the archive
  if (!hasMesh) {
    modelPaths.forEach((p) => {
      const m = parseModel(p); if (!m) return;
      Object.values(m.objects).forEach((o) => { addObjectMeshes(root, o); hasMesh = true; });
    });
  }
  if (!hasMesh) throw new Error("3MF: tidak ada mesh yang bisa ditampilkan");
  return root;
}

function effective(baseInfo, s) {
  const areaScale = (Math.abs(s.x * s.y) + Math.abs(s.y * s.z) + Math.abs(s.x * s.z)) / 3 || 1;
  return { volume_cm3: baseInfo.volume_cm3 * s.x * s.y * s.z, surface_cm2: (baseInfo.surface_cm2 || 0) * areaScale, bbox: [baseInfo.bbox[0] * s.x, baseInfo.bbox[1] * s.y, baseInfo.bbox[2] * s.z], triangles: baseInfo.triangles };
}

function buildReport(info, integ, splitModules = [20, 30, 40]) {
  const checks = [];
  const [bx, by, bz] = info.bbox;
  const fits = bx <= BUILD_VOLUME.x && by <= BUILD_VOLUME.y && bz <= BUILD_VOLUME.z;
  const mods = (splitModules || [20, 30, 40]).join(", ");
  checks.push({ key: "fit", label: "Ukuran vs printer", level: fits ? "pass" : "info",
    desc: `${bx.toFixed(1)} × ${by.toFixed(1)} × ${bz.toFixed(1)} mm` + (fits ? ` — muat di build volume ${BUILD_VOLUME.x}×${BUILD_VOLUME.y}×${BUILD_VOLUME.z} mm.` : ` melebihi build volume ${BUILD_VOLUME.x}×${BUILD_VOLUME.y}×${BUILD_VOLUME.z} mm. Tidak masalah — akan dilakukan split printing per modul ${mods} cm lalu disatukan.`) });
  if (integ) {
    checks.push({ key: "watertight", label: "Watertight", level: integ.boundary > 0 ? "error" : "pass",
      desc: integ.boundary > 0 ? `Open mesh: ${integ.boundary} tepi terbuka. Perlu perbaikan (repair) oleh tim sebelum cetak.` : "Mesh tertutup rapat (tidak ada lubang)." });
    checks.push({ key: "manifold", label: "Manifold edges", level: integ.nonmanifold > 0 ? "warn" : "pass",
      desc: integ.nonmanifold > 0 ? `${integ.nonmanifold} tepi non-manifold (dipakai lebih dari dua face).` : "Setiap tepi tersambung tepat ke dua face." });
    checks.push({ key: "selfint", label: "Self-intersection", level: integ.selfint == null ? "info" : integ.selfint > 0 ? "warn" : "pass",
      desc: integ.selfint == null ? "Tidak dihitung otomatis (file sangat besar)." : integ.selfint > 0 ? `${integ.selfint} segitiga saling beririsan — perlu tinjauan tim (slicer modern umumnya tetap bisa mencetak).` : "Tidak ada segitiga yang saling beririsan." });
  }
  const maxDim = Math.max(bx, by, bz);
  checks.push({ key: "size", label: "Satuan", level: maxDim > 1000 ? "warn" : "pass",
    desc: maxDim > 1000 ? `${maxDim.toFixed(1)} mm — jika seharusnya lebih kecil, mungkin terekspor dalam inch/meter (kami baca file dalam mm).` : "Ukuran dalam rentang wajar (mm)." });
  const thin = info.bbox.some((d) => d < 1);
  checks.push({ key: "thin", label: "Dinding tipis / celah", level: thin ? "warn" : "pass",
    desc: thin ? "Ada dimensi < 1 mm — fitur sangat tipis berisiko gagal cetak (min. dinding FDM 0.8 mm)." : "Tidak ada fitur di bawah 0.8 mm." });
  const solid = info.volume_cm3 > 0.0001;
  checks.push({ key: "solid", label: "Volume solid", level: solid ? "pass" : "error",
    desc: solid ? `Volume ${info.volume_cm3.toFixed(2)} cm³.` : "Volume ~0 — model mungkin tidak solid/manifold, perlu repair." });
  const simple = info.triangles < 50;
  checks.push({ key: "faces", label: "Jumlah face", level: "info",
    desc: simple ? `${info.triangles.toLocaleString()} faces — model sederhana (face sedikit), tidak termasuk kategori perlu perbaikan.` : `${info.triangles.toLocaleString()} faces.` });
  return checks;
}

const LEVEL = {
  error: { Icon: XCircle, cls: "text-rose-400" },
  warn: { Icon: AlertTriangle, cls: "text-amber-400" },
  pass: { Icon: CheckCircle2, cls: "text-slate-500" },
  info: { Icon: Info, cls: "text-sky-400" },
};

function PrintabilityReport({ checks }) {
  const [showPassed, setShowPassed] = useState(false);
  const issues = checks.filter((c) => c.level === "error" || c.level === "warn");
  const info = checks.filter((c) => c.level === "info");
  const passed = checks.filter((c) => c.level === "pass");
  const hasError = checks.some((c) => c.level === "error");
  const Row = ({ c }) => {
    const { Icon, cls } = LEVEL[c.level];
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cls}`} />
        <div><div className="text-sm text-slate-100">{c.label}</div><div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{c.desc}</div></div>
      </div>
    );
  };
  return (
    <div className="bg-slate-900 text-slate-100 rounded-2xl p-4" data-testid="printability-panel">
      <div className="text-[10px] font-mono text-slate-500 tracking-wider">FILE CHECK</div>
      <div className="flex items-center gap-2 mt-1 mb-2">
        {hasError ? <AlertTriangle className="w-5 h-5 text-amber-400" /> : issues.length ? <AlertTriangle className="w-5 h-5 text-amber-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
        <div className="font-head font-semibold">
          {hasError ? `File terindikasi bermasalah — bisa tetap request (repair dinilai tim)` : issues.length ? `${issues.length} catatan` : "Siap cetak — semua lolos"}
        </div>
      </div>
      {[...issues, ...info].map((c) => <Row key={c.key} c={c} />)}
      {passed.length > 0 && (
        <>
          <button onClick={() => setShowPassed((s) => !s)} data-testid="toggle-passed" className="flex items-center gap-1.5 text-xs font-mono text-slate-400 hover:text-slate-200 mt-2">
            {passed.length} pengecekan lolos <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPassed ? "rotate-180" : ""}`} />
          </button>
          {showPassed && <div className="mt-1">{passed.map((c) => <Row key={c.key} c={c} />)}</div>}
        </>
      )}
      {hasError && <p className="text-xs text-slate-400 mt-3 leading-relaxed">File yang bermasalah tetap bisa di-<b>Get Final Quote</b>. Harga dasar dihitung dari volume × material; biaya perbaikan (repair) akan dinilai & dikonfirmasi tim setelah pengecekan.</p>}
    </div>
  );
}

export default function Calculator({ embedded = false }) {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const geomRef = useRef({}); // id -> { object(pivotGroup), baseInfo, integ, colorCount }
  const [parts, setParts] = useState([]); // {id,name,process,materialId,infill,quantity, s:{x,y,z}, uniform, multicolor, multicolorMode, color}
  const [uploadMsg, setUploadMsg] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [designCfg, setDesignCfg] = useState({ multicolor_3mf: { per_gram: 5000, per_run: 600000 }, split_modules: [20, 30, 40] });
  const [rush, setRush] = useState(false);
  const [combine, setCombine] = useState(false);
  const [wireframe, setWireframe] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [quote, setQuote] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [contacts, setContacts] = useState({ email: "info@fomu.co.id", phone: "081210800062" });
  const [form, setForm] = useState({ customer_name: "", entity_type: "Perusahaan", company: "", whatsapp: "", email: "", project_name: "", notes: "" });
  // Member FOMU (JWT) — edit quotation (biaya tambahan) dari footer
  const [memberTok, setMemberTok] = useState(() => localStorage.getItem("fomu_member_token") || "");
  const [member, setMember] = useState(null);
  const shop = useShop();
  const [staff, setStaff] = useState(embedded);
  const [gate, setGate] = useState("");
  useEffect(() => { if (!embedded) { api.get("/auth/me").then(() => setStaff(true)).catch(() => {}); } }, [embedded]);
  const authed = !!(shop.me || member || staff);
  const authH = memberTok ? { Authorization: `Bearer ${memberTok}` } : {};
  const needLogin = (r) => { if (authed) return false; setGate(r); return true; };
  const [showMemberLogin, setShowMemberLogin] = useState(false);
  const [memberForm, setMemberForm] = useState({ email: "", password: "" });
  const [memberBusy, setMemberBusy] = useState(false);
  const [addCosts, setAddCosts] = useState([]); // [{label, qty, price, note}]
  const [custList, setCustList] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [showCustDrop, setShowCustDrop] = useState(false);
  const [memberRoles, setMemberRoles] = useState(["owner", "sales", "finance"]);
  const [taxDef, setTaxDef] = useState({ name: "PPN", type: "preset", rate: 11, dpp_num: 1, dpp_den: 1, mode: "exclusive" });
  const [expCfg, setExpCfg] = useState({ pct: 50, min: 300000 });

  useEffect(() => {
    if (!memberTok) { setMember(null); return; }
    axios.get(`${API_BASE}/member/me`, { headers: { Authorization: `Bearer ${memberTok}` } })
      .then(({ data }) => {
        setMember(data);
        axios.get(`${API_BASE}/member/customers`, { headers: { Authorization: `Bearer ${memberTok}` } }).then(({ data: cs }) => setCustList(cs)).catch(() => {});
      })
      .catch(() => { setMember(null); setMemberTok(""); localStorage.removeItem("fomu_member_token"); });
  }, [memberTok]);

  const memberLogin = async () => {
    setMemberBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/member/login`, { email: memberForm.email, password: memberForm.password });
      localStorage.setItem("fomu_member_token", data.token);
      setMemberTok(data.token); setMember(data.member);
      setShowMemberLogin(false); setMemberForm({ email: "", password: "" });
      toast.success(`Masuk sebagai member FOMU (${data.member.role})`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login gagal");
    }
    setMemberBusy(false);
  };
  const memberLogout = () => { setMemberTok(""); setMember(null); localStorage.removeItem("fomu_member_token"); setAddCosts([]); setCustList([]); setCustQuery(""); toast.success("Keluar dari mode member"); };
  const pickCustomer = (c) => {
    setForm({ ...form, customer_name: c.name || "", company: c.company || "", whatsapp: c.whatsapp || "", email: c.email || "" });
    setCustQuery(c.name || ""); setShowCustDrop(false);
  };
  const addCostRow = (label = "") => setAddCosts((a) => [...a, { label, qty: 1, price: "", note: "" }]);
  const setCostRow = (i, patch) => setAddCosts((a) => a.map((c, j) => j === i ? { ...c, ...patch } : c));
  const delCostRow = (i) => setAddCosts((a) => a.filter((_, j) => j !== i));

  useEffect(() => {
    const loadMats = (attempt = 0) => api.get("/materials")
      .then(({ data }) => { if (Array.isArray(data) && data.length) setMaterials(data); else if (attempt < 3) setTimeout(() => loadMats(attempt + 1), 800); })
      .catch(() => { if (attempt < 3) setTimeout(() => loadMats(attempt + 1), 800); else toast.error("Gagal memuat daftar material — muat ulang halaman"); });
    loadMats();
    api.get("/settings/booking").then(({ data }) => { if (data?.email || data?.phone) setContacts({ email: data.email || "info@fomu.co.id", phone: data.phone || "081210800062" }); }).catch(() => {});
    api.get("/settings/design").then(({ data }) => { if (data?.multicolor_3mf) setDesignCfg(data); }).catch(() => {});
    api.get("/settings/member-rules").then(({ data }) => { if (data?.edit_roles?.length) setMemberRoles(data.edit_roles); }).catch(() => {});
    api.get("/settings/pricing").then(({ data }) => { setTaxDef(data?.tax_default || null); setExpCfg({ pct: data?.rush_fee_percent ?? 50, min: data?.express_minimum ?? 300000 }); }).catch(() => {});
  }, []);

  // Backfill default material once the list loads — fixes "Rp 0" when a file is added before materials arrive or the list failed to load
  useEffect(() => {
    if (!materials.length) return;
    setParts((prev) => {
      let changed = false;
      const np = prev.map((p) => {
        if (p.materialId && materials.some((m) => m.id === p.materialId)) return p;
        const dm = materials.find((m) => m.process === p.process) || materials[0];
        if (!dm) return p;
        changed = true; return { ...p, materialId: dm.id, process: dm.process || p.process };
      });
      return changed ? np : prev;
    });
  }, [materials]);

  const matsByProcess = useCallback((proc) => materials.filter((m) => m.process === proc), [materials]);
  const active = parts.find((p) => p.id === activeId);

  useEffect(() => {
    const mount = mountRef.current; if (!mount) return;
    const w = mount.clientWidth, h = mount.clientHeight;
    const scene = new THREE.Scene(); scene.background = new THREE.Color(0xdfe8ef);
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 5000); camera.position.set(80, 80, 120);
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h); renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement); controls.enableDamping = true;
    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dir = new THREE.DirectionalLight(0xffffff, 1.1); dir.position.set(1, 1.5, 1); scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.4); dir2.position.set(-1, -0.5, -1); scene.add(dir2);
    const grid = new THREE.GridHelper(400, 20, 0x94a3b8, 0xcbd5e1); scene.add(grid);
    stateRef.current = { scene, camera, renderer, controls, grid, mount, mesh: null, autoRotate: true };
    let raf; const animate = () => { raf = requestAnimationFrame(animate); controls.autoRotate = stateRef.current.autoRotate; controls.autoRotateSpeed = 2; controls.update(); renderer.render(scene, camera); };
    animate();
    const onResize = () => { const nw = mount.clientWidth, nh = mount.clientHeight; camera.aspect = nw / nh; camera.updateProjectionMatrix(); renderer.setSize(nw, nh); };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); renderer.dispose(); try { mount.removeChild(renderer.domElement); } catch {} };
  }, []);

  useEffect(() => { stateRef.current.autoRotate = autoRotate; }, [autoRotate]);
  useEffect(() => {
    const s = stateRef.current; if (!s.mesh) return;
    if (s.mesh.traverse) s.mesh.traverse((c) => { if (c.isMesh && c.material && !Array.isArray(c.material)) c.material.wireframe = wireframe; });
  }, [wireframe]);

  // apply chosen solid color to the preview (single-color parts only; AMS/multicolor keeps file colors)
  useEffect(() => {
    const st = stateRef.current; const p = active;
    if (!st.mesh || !p || p.multicolor) return;
    const c = new THREE.Color(p.color?.hex || "#2a7d66");
    st.mesh.traverse((o) => { if (o.isMesh && o.material && !Array.isArray(o.material)) o.material.color.copy(c); });
  }, [active?.color?.hex, active?.multicolor, activeId]);

  const frameCamera = (obj) => {
    const s = stateRef.current;
    const box = new THREE.Box3().setFromObject(obj); const size = new THREE.Vector3(); box.getSize(size);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const r = Math.max(sphere.radius, 1);
    const dist = r * 2.1;
    s.camera.position.set(dist * 0.7, dist * 0.65, dist);
    s.camera.near = Math.max(0.1, r / 100); s.camera.far = dist * 10; s.camera.updateProjectionMatrix();
    s.controls.target.copy(sphere.center);
    // batas zoom: kamera tidak bisa masuk ke dalam model (min) atau terlalu jauh hingga model hilang (max)
    s.controls.minDistance = r * 0.8;
    s.controls.maxDistance = r * 12;
    s.controls.update();
    s.grid.scale.setScalar(Math.max(1, Math.max(size.x, size.z) / 200));
    if (s.grid.scale.x > 1) s.grid.position.set(sphere.center.x, 0, sphere.center.z);
  };

  const showModel = useCallback((entry, s) => {
    const st = stateRef.current;
    if (st.mesh) { st.scene.remove(st.mesh); }
    const obj = entry.object; // pre-centered pivot group
    obj.scale.set(s.x, s.y, s.z); st.scene.add(obj); st.mesh = obj;
    frameCamera(obj);
  }, []);

  useEffect(() => {
    const st = stateRef.current;
    if (st.mesh && active) { st.mesh.scale.set(active.s.x, active.s.y, active.s.z); frameCamera(st.mesh); }
  }, [active?.s.x, active?.s.y, active?.s.z]);

  // Auto zoom / refresh: re-frame kamera; jika visual hilang/rusak, pasang ulang model lalu render
  const resetView = () => {
    const st = stateRef.current;
    if (!st.scene) return;
    const entry = active ? geomRef.current[active.id] : null;
    if (entry && active && (!st.mesh || !st.mesh.parent)) {
      showModel(entry, active.s);
    } else if (st.mesh) {
      frameCamera(st.mesh);
    }
    if (st.renderer && st.camera) st.renderer.render(st.scene, st.camera);
  };

  const addPart = (entry, fileName) => {
    const id = `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    geomRef.current[id] = entry;
    const defMat = (matsByProcess("FDM")[0] || materials[0]);
    const rep0 = computeRepair(entry.baseInfo, entry.integ);
    const part = { id, name: fileName, process: defMat?.process || "FDM", materialId: defMat?.id || "", infill: 15, quantity: 1,
      s: { x: 1, y: 1, z: 1 }, uniform: true,
      color: { name: "Putih", hex: "#FFFFFF" },
      repairNeeded: rep0.needed, repairTierKey: "quick", repairPeople: 1, repairMetrics: rep0.metrics };
    setParts((prev) => [...prev, part]);
    setActiveId(id); showModel(entry, part.s);
    setAnalyzing(false);
    const rep = buildReport(entry.baseInfo, entry.integ, designCfg.split_modules);
    if (rep.some((c) => c.level === "error")) toast.warning(`"${fileName}" terindikasi bermasalah — tetap bisa request (repair dinilai tim).`);
    else toast.success(`Model "${fileName}" dimuat, di-center & diletakkan rata di bed.`);
  };

  const loadOneFile = (file) => new Promise((resolve) => {
    if (file.size > 100 * 1024 * 1024) { toast.error(`File "${file.name}" melebihi 100MB`); return resolve(); }
    if (!authed && file.size > GUEST_MAX_MB * 1024 * 1024) { setGate(`File "${file.name}" lebih dari ${GUEST_MAX_MB} MB. Log in dulu untuk simulasi file besar, atau kirim lewat WA tim kami.`); return resolve(); }
    const ext = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        let content; let integ = null; let colorCount = 1;
        if (ext === "stl") {
          const geom = new STLLoader().parse(ev.target.result);
          if (!geom?.attributes?.position) throw new Error("geometry kosong");
          integ = meshIntegrity(geom);
          content = new THREE.Mesh(geom, greenMat());
        } else if (ext === "obj") {
          const obj = new OBJLoader().parse(ev.target.result);
          let any = false;
          obj.traverse((c) => { if (c.isMesh) { any = true; c.material = greenMat(); } }); // semua object, bukan cuma 1
          if (!any) throw new Error("OBJ tidak berisi mesh");
          content = obj;
        } else if (ext === "3mf") {
          try {
            content = new ThreeMFLoader().parse(ev.target.result);
          } catch (err) {
            console.warn("ThreeMFLoader gagal, pakai fallback manual:", err);
            content = parse3MFManual(ev.target.result); // robust fallback untuk file slicer (Bambu/Prusa/Orca)
          }
          let any = false; content.traverse((c) => { if (c.isMesh) any = true; });
          if (!any) content = parse3MFManual(ev.target.result);
        } else {
          toast.error("Preview mendukung STL/OBJ/3MF. STEP diproses manual oleh tim.");
          return resolve();
        }
        if (!integ) integ = objectIntegrity(content);
        autoOrient(content); // auto flat bed: permukaan datar terbesar ke bed
        const info = analyzeObject(content);
        colorCount = info.colorCount;
        if (info.triangles <= 0) throw new Error("Tidak ada geometri yang valid");
        const pivot = wrapBed(content); // center + flat to bed
        addPart({ object: pivot, baseInfo: { volume_cm3: info.volume_cm3, surface_cm2: info.surface_cm2, bbox: info.bbox, triangles: info.triangles }, integ, colorCount, file }, file.name);
      } catch (err) {
        console.error(err);
        toast.error(`File "${file.name}" tidak bisa dibaca. Pastikan file valid & tidak rusak.`);
      }
      resolve();
    };
    reader.onerror = () => { toast.error(`Gagal membaca file "${file.name}"`); resolve(); };
    if (ext === "stl" || ext === "3mf") reader.readAsArrayBuffer(file); else reader.readAsText(file);
  });

  // multi-file: pilih beberapa file sekaligus — tiap file diproses berurutan & jadi part terpisah
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setAnalyzing(true);
    for (const f of files) { await loadOneFile(f); }
    setAnalyzing(false);
  };

  const selectPart = (id) => { setActiveId(id); const g = geomRef.current[id]; const p = parts.find((x) => x.id === id); if (g) showModel(g, p?.s || { x: 1, y: 1, z: 1 }); };
  const removePart = (id) => {
    const rest = parts.filter((p) => p.id !== id);
    setParts(rest); delete geomRef.current[id];
    if (activeId === id) { const nx = rest[rest.length - 1]; if (nx) selectPart(nx.id); else { const s = stateRef.current; if (s.mesh) { s.scene.remove(s.mesh); s.mesh = null; } setActiveId(null); } }
  };
  const updatePart = (id, patch) => setParts((prev) => prev.map((p) => {
    if (p.id !== id) return p;
    const np = { ...p, ...patch };
    if (patch.process && patch.process !== p.process) { const fm = matsByProcess(patch.process)[0]; np.materialId = fm?.id || ""; }
    if (patch.process === "SLA" && !SLA_COLORS.some((c) => c.name === np.color?.name)) np.color = { name: "Putih", hex: "#FFFFFF" };
    return np;
  }));

  const setScaleAxis = (id, axis, factor) => {
    const p = parts.find((x) => x.id === id); if (!p) return;
    factor = Math.max(0.01, factor || 0.01);
    const s = p.uniform ? { x: factor, y: factor, z: factor } : { ...p.s, [axis]: factor };
    updatePart(id, { s });
  };
  const setSizeAxis = (id, axis, mm) => {
    const p = parts.find((x) => x.id === id); const g = geomRef.current[id]; if (!p || !g) return;
    const idx = { x: 0, y: 1, z: 2 }[axis];
    const base = g.baseInfo.bbox[idx] || 1;
    setScaleAxis(id, axis, Math.max(1, mm || 1) / base);
  };

  const reports = useMemo(() => parts.map((p) => {
    const g = geomRef.current[p.id]; if (!g) return { part: p, checks: [] };
    return { part: p, checks: buildReport(effective(g.baseInfo, p.s), g.integ, designCfg.split_modules) };
  }), [parts, designCfg]);
  const hasError = reports.some((r) => r.checks.some((c) => c.level === "error"));
  const designRate = designCfg.rate_per_hour || 100000;
  const rnd100c = (x) => Math.round((x || 0) / 100) * 100;
  const tiers = useMemo(() => repairTiers(designCfg), [designCfg]);
  const repairTierOf = (p) => (p?.repairNeeded ? (tiers.find((t) => t.key === p.repairTierKey) || tiers[0]) : null);
  const repairTotal = parts.reduce((s, p) => s + (repairTierOf(p)?.price || 0) * repairPeopleOf(p), 0) * (rush ? 2 : 1);
  const repairHoursRaw = parts.reduce((s, p) => s + (repairTierOf(p)?.hours || 0), 0);
  const repairPeopleMax = parts.reduce((m, p) => Math.max(m, p?.repairNeeded ? repairPeopleOf(p) : 1), 1);
  const repairHoursEff = repairHoursRaw > 0 ? Math.max(3, repairHoursRaw / repairPeopleMax) : 0;
  const addCostTotal = member ? addCosts.reduce((s, c) => s + ((+c.qty || 1) * (+c.price || 0)), 0) : 0;
  const custMatches = custList.filter((c) => { const q = custQuery.toLowerCase(); return !q || [c.name, c.company, c.whatsapp, c.email].some((v) => (v || "").toLowerCase().includes(q)); }).slice(0, 6);
  const preTaxTotal = (quote?.total || 0) + repairTotal + addCostTotal;
  const { tax: taxAmt, total: grandTotal } = calcTax(preTaxTotal, taxDef);

  const calcKey = JSON.stringify(parts.map((p) => [p.materialId, p.infill, p.quantity, p.s.x, p.s.y, p.s.z, p.multicolor, p.multicolorMode, p.repairNeeded, p.repairPeople])) + rush + combine;
  const calculate = useCallback(async () => {
    const valid = parts.filter((p) => p.materialId && geomRef.current[p.id]);
    if (valid.length === 0) { setQuote(null); return; }
    setCalcLoading(true);
    try {
      const payload = {
        rush, combine_run: combine,
        repair_hours: repairHoursRaw, repair_people: repairPeopleMax,
        parts: valid.map((p) => {
          const eff = effective(geomRef.current[p.id].baseInfo, p.s);
          return { name: p.name, material_id: p.materialId, volume_cm3: eff.volume_cm3, surface_cm2: eff.surface_cm2, bbox: eff.bbox, infill: p.infill, quantity: p.quantity, scale: p.s.x, multicolor: !!p.multicolor, multicolor_mode: p.multicolorMode, has_issue: !!p.repairNeeded, repair_tier: p.repairTierKey || "", repair_people: p.repairPeople || 1 };
        }),
      };
      const { data } = await api.post("/quote/calculate-batch", payload);
      setQuote(data);
    } catch { toast.error("Gagal menghitung"); }
    setCalcLoading(false);
  }, [calcKey]);
  useEffect(() => { calculate(); }, [calculate]);

  const captureThumb = () => {
    try {
      const s = stateRef.current; if (!s.mesh) return null;
      const box = new THREE.Box3().setFromObject(s.mesh); const size = new THREE.Vector3(); box.getSize(size);
      const center = new THREE.Vector3(); box.getCenter(center); const d = (Math.max(size.x, size.y, size.z) || 1) * 1.9;
      const sp = s.camera.position.clone(), st = s.controls.target.clone();
      s.camera.position.set(center.x + d, center.y + d * 0.75, center.z + d); s.camera.lookAt(center); s.controls.target.copy(center); s.controls.update();
      s.renderer.render(s.scene, s.camera); const url = s.renderer.domElement.toDataURL("image/png");
      s.camera.position.copy(sp); s.controls.target.copy(st); s.controls.update(); return url;
    } catch { return null; }
  };

  const captureAllThumbs = () => {
    const st = stateRef.current; if (!st.scene) return [];
    const out = [];
    const curActive = active; const curEntry = curActive ? geomRef.current[curActive.id] : null;
    const validParts = parts.filter((p) => p.materialId && geomRef.current[p.id]);
    for (const p of validParts) {
      const entry = geomRef.current[p.id];
      showModel(entry, p.s);
      const img = captureThumb();
      const eff = effective(entry.baseInfo, p.s);
      const line = (quote?.lines || [])[validParts.indexOf(p)];
      if (img) out.push({ name: p.name, image: img, size_mm: eff ? eff.bbox.map((v) => +v.toFixed(1)) : null, weight_g: line ? line.weight_g : null });
    }
    if (curEntry && curActive) showModel(curEntry, curActive.s);
    return out;
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const navigate = useNavigate();
  const savingQuote = useRef(false);
  const saveQuoteAndEdit = async (direct = false) => {
    if (!direct && (!form.customer_name || (!form.whatsapp && !form.email))) { toast.error("Isi nama & kontak (WA/email)"); return; }
    if (!quote) { toast.error("Belum ada estimasi"); return; }
    if (needLogin("Log in dulu untuk menyimpan quotation dan mengirim file 3D.")) return;
    if (savingQuote.current) return;
    savingQuote.current = true;
    try {
      const { payload } = buildQuotePayload();
      if (direct && !form.customer_name) payload.customer_name = "Customer Baru";
      const { data } = await api.post("/quotations", payload, { headers: authH });
      // upload file 3D (await) agar tertaut sebelum membuka editor
      try {
        const CH = 4 * 1024 * 1024;
        const vp = parts.filter((p) => geomRef.current[p.id]?.file);
        for (const p of vp) {
          const f = geomRef.current[p.id].file; const total = Math.max(1, Math.ceil(f.size / CH));
          for (let i = 0; i < total; i++) {
            const fd = new FormData();
            fd.append("quote_id", data.id); fd.append("filename", f.name);
            fd.append("index", i); fd.append("total", total);
            fd.append("chunk", f.slice(i * CH, (i + 1) * CH), f.name);
            await api.post("/public/quote-upload", fd, { headers: { "Content-Type": "multipart/form-data", ...authH } });
          }
        }
      } catch { /* abaikan kegagalan upload */ }
      toast.success(`Quotation ${data.code} dibuat — membuka quotation`);
      navigate(direct ? `/app/quotations?open=${data.id}` : `/app/quotations?open=${data.id}&edit=1`);
    } catch { toast.error("Gagal menyimpan quotation"); }
    savingQuote.current = false;
  };
  const buildQuotePayload = () => {
    const notes = (repairTotal > 0 ? `[REPAIR OTOMATIS: ${formatIDR(repairTotal)}] ` : "") + (form.notes || "");
    const vparts = parts.filter((p) => p.materialId && geomRef.current[p.id]);
    const coloredQuote = { ...quote, lines: (quote.lines || []).map((l, i) => {
      const p = vparts[i];
      let base = l;
      if (p) {
        const g = geomRef.current[p.id];
        const eff = g ? g.baseInfo : null;
        base = { ...l, fromFile: true, fileName: p.name, materialId: p.materialId, infill: p.infill, scale: (p.s?.x ?? 1), ...(eff ? { volume_cm3: eff.volume_cm3, surface_cm2: eff.surface_cm2, bbox: eff.bbox } : {}) };
      }
      return p && !p.multicolor && p.color ? { ...base, name: `${base.name} · Warna: ${p.color.name}`, color: p.color.name, color_hex: p.color.hex } : (p && p.multicolor ? { ...base, color: "Multicolor (AMS)" } : base);
    }) };
    const payload = {
      customer_name: form.customer_name || "Preview Customer",
      company: form.entity_type === "Pribadi" ? "" : form.company,
      entity_type: form.entity_type,
      whatsapp: form.whatsapp, email: form.email,
      project_name: form.project_name, notes,
      file_name: parts.map((p) => p.name).join(", "),
      quote: coloredQuote, thumbnail: captureThumb(), thumbnails: captureAllThumbs(), repair_cost: repairTotal,
      repair_meta: repairTotal > 0 ? { hours: repairHoursEff, level: (tiers.slice().reverse().find((t) => parts.some((p) => repairTierOf(p)?.key === t.key))?.label || "QUICK"), price: repairTotal, rate: designRate, people: repairPeopleMax } : null,
      quote_input: { rush, combine_run: combine, parts: parts.map((p) => ({ name: p.name, material_id: p.materialId, infill: p.infill, quantity: p.quantity, scale: p.s, multicolor: !!p.multicolor, multicolor_mode: p.multicolorMode })) },
      additional_costs: member ? addCosts.filter((c) => (c.label || "").trim() || +c.price || (c.note || "").trim()).map((c) => ({ label: c.label || "Biaya Tambahan", qty: +c.qty || 1, price: +c.price || 0, note: c.note || "" })) : [],
    };
    return { coloredQuote, payload };
  };
  const fetchQuotePdf = async () => {
    const { payload } = buildQuotePayload();
    const { data } = await api.post("/public/quotation/preview-pdf", payload, { responseType: "blob" });
    return new Blob([data], { type: "application/pdf" });
  };
  const previewPdf = async () => {
    setPdfBusy(true);
    try { const blob = await fetchQuotePdf(); window.open(URL.createObjectURL(blob), "_blank"); }
    catch { toast.error("Gagal membuat preview PDF"); }
    setPdfBusy(false);
  };
  const downloadPdf = async () => {
    if (!quote) { toast.error("Belum ada estimasi"); return; }
    if (needLogin("Log in dulu untuk download PDF quotation.")) return;
    setPdfBusy(true);
    try {
      // Member download → buat quotation resmi agar nomor ID langsung terbentuk, lalu unduh PDF final ber-ID tsb
      const { payload } = buildQuotePayload();
      const { data: qd } = await api.post("/quotations", payload, { headers: authH });
      const { data } = await api.get(`/public/quotation/${qd.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${qd.code}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Quotation ${qd.code} terbentuk & PDF ter-download`);
    } catch { toast.error("Gagal men-download PDF"); }
    setPdfBusy(false);
  };
  const submitQuote = async () => {
    if (!form.customer_name || (!form.whatsapp && !form.email)) { toast.error("Isi nama & kontak (WA/email)"); return; }
    if (!quote) { toast.error("Belum ada estimasi"); return; }
    if (needLogin("Log in dulu untuk mengirim quotation dan file 3D ke tim FOMU.")) return;
    if (parts.filter((p) => geomRef.current[p.id]?.file).length > 10) { toast.error("Maksimal 10 file per quotation"); return; }
    try {
      const { coloredQuote, payload } = buildQuotePayload();
      const { data } = await api.post("/quotations", payload, { headers: authH });
      const codeId = data.code;
      // Upload file 3D (chunked) ke server lalu kirim email otomatis ke info@fomu.co.id
      (async () => {
        try {
          const CH = 4 * 1024 * 1024;
          const vp = parts.filter((p) => geomRef.current[p.id]?.file);
          for (const p of vp) {
            const f = geomRef.current[p.id].file; const total = Math.max(1, Math.ceil(f.size / CH));
            for (let i = 0; i < total; i++) {
              const fd = new FormData();
              fd.append("quote_id", data.id); fd.append("filename", f.name);
              fd.append("index", i); fd.append("total", total);
              fd.append("chunk", f.slice(i * CH, (i + 1) * CH), f.name);
              await api.post("/public/quote-upload", fd, { headers: { "Content-Type": "multipart/form-data", ...authH } });
              setUploadMsg(`Mengunggah ${f.name} ke FOMU… ${Math.round(((i + 1) / total) * 100)}%`);
            }
          }
          await api.post(`/quotations/${data.id}/notify`);
          setUploadMsg(""); toast.success("File 3D terkirim ke info@fomu.co.id");
        } catch (e) { setUploadMsg(""); }
      })();
      // Email dikirim otomatis server-side (dengan PDF attachment) via endpoint /notify di atas.
      const waText = `Halo FOMU 3D PRINT, saya request quotation. ID: ${codeId}. Nama: ${form.customer_name}. Total estimasi: ${formatIDR(grandTotal)}. Saya juga akan membawa/kirim data model yang ingin dibuat.`;
      window.open(`https://wa.me/${waNumber(contacts.phone)}?text=${encodeURIComponent(waText)}`, "_blank");
      toast.success(`Quotation ${codeId} dibuat. WhatsApp dibuka & email (PDF terlampir) sedang dikirim otomatis.`);
      setShowSummary(false);
    } catch { toast.error("Gagal membuat quotation"); }
  };

  const activeMats = active ? matsByProcess(active.process) : [];
  const activeEntry = active ? geomRef.current[active.id] : null;
  const activeEff = active && activeEntry ? effective(activeEntry.baseInfo, active.s) : null;
  const activeReport = reports.find((r) => r.part.id === activeId)?.checks || [];
  const AX = [["x", "X"], ["y", "Y"], ["z", "Z"]];

  return (
    <div>
      {!embedded && <PublicNav />}
      {gate && <LoginGate reason={gate} phone={contacts.phone} close={() => setGate("")} />}
      {!embedded && !authed && <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm px-4 py-2 text-center" data-testid="calc-guest-banner">Mode tamu: simulasi file sampai {GUEST_MAX_MB} MB. <button type="button" onClick={() => setGate("Log in untuk file sampai 100 MB, kirim quotation & download PDF.")} className="underline font-semibold" data-testid="calc-guest-login">Log in</button> untuk file lebih besar & kirim quotation.</div>}
      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-5 py-6 sm:py-8"}>
        {!embedded && <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Kalkulator Harga 3D Printing</h1>}
        {!embedded && <p className="text-slate-600 mt-1 text-sm">Upload beberapa file — tiap file punya setting produksi sendiri. Preview mengikuti isi file (STL/OBJ/3MF, warna bawaan 3MF), di-center & rata di bed. <span className="text-xs font-mono text-amber-600">ESTIMASI</span></p>}

        <div className={`grid lg:grid-cols-[1.4fr_1fr] gap-5 sm:gap-6 ${embedded ? "" : "mt-6"}`}>
          {/* Viewer */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
              <div ref={mountRef} data-testid="viewer-canvas" className="relative w-full" style={{ height: "380px" }}>
                {parts.length === 0 && !analyzing && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none"><Box className="w-12 h-12 mb-3" /><p className="text-sm">Preview model 3D (center & rata di bed, sesuai orientasi file)</p></div>
                )}
                {analyzing && <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur"><div className="flex items-center gap-2 text-slate-700"><Loader2 className="w-5 h-5 animate-spin" /> Analyzing…</div></div>}
                {activeEff && <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-slate-900/80 text-white text-[11px] font-mono px-3 py-1 rounded-full" data-testid="dim-readout">W, D, H: {activeEff.bbox[0].toFixed(1)}, {activeEff.bbox[1].toFixed(1)}, {activeEff.bbox[2].toFixed(1)} mm</div>}
              </div>
              <div className="flex items-center gap-2 p-3 border-t border-slate-200 flex-wrap">
                <button data-testid="toggle-rotate" onClick={() => setAutoRotate(!autoRotate)} className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border ${autoRotate ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}><RotateCw className="w-4 h-4" /> Rotate</button>
                <button data-testid="toggle-wireframe" onClick={() => setWireframe(!wireframe)} className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border ${wireframe ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}><Grid3x3 className="w-4 h-4" /> Wireframe</button>
                <button data-testid="reset-view-btn" onClick={resetView} title="Auto zoom / refresh tampilan jika model hilang atau rusak" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:border-sky-500 hover:text-sky-600 transition-colors"><Focus className="w-4 h-4" /> Auto Zoom</button>
              </div>
            </div>

            {/* Bambu-style transform panel */}
            {active && activeEntry && (
              <div className="bg-slate-900 text-slate-100 rounded-2xl p-4" data-testid="transform-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono text-slate-400 tracking-wider">TRANSFORM · SCALE</div>
                  <button data-testid="uniform-toggle" onClick={() => updatePart(active.id, { uniform: !active.uniform })} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border ${active.uniform ? "border-sky-500 text-sky-400" : "border-slate-600 text-slate-300"}`}>
                    {active.uniform ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />} Uniform scale
                  </button>
                </div>
                <div className="grid grid-cols-[20px_1fr_1fr] gap-x-3 gap-y-2 items-center">
                  <div className="text-[10px] font-mono text-slate-500" />
                  <div className="text-[10px] font-mono text-slate-500">Scale</div>
                  <div className="text-[10px] font-mono text-slate-500">Size (mm)</div>
                  {AX.map(([ax, lbl], i) => (
                    <React.Fragment key={ax}>
                      <div className={`text-sm font-bold ${["text-rose-400", "text-emerald-400", "text-sky-400"][i]}`}>{lbl}</div>
                      <input data-testid={`scale-${ax}`} type="number" step="0.01" min="0.01" value={+active.s[ax].toFixed(3)} onChange={(e) => setScaleAxis(active.id, ax, +e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm w-full" />
                      <input data-testid={`size-${ax}`} type="number" step="0.1" min="1" value={+activeEff.bbox[i].toFixed(2)} onChange={(e) => setSizeAxis(active.id, ax, +e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm w-full" />
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex gap-1.5 mt-3">
                  {[50, 100, 200].map((pct) => (<button key={pct} data-testid={`scale-preset-${pct}`} onClick={() => updatePart(active.id, { uniform: true, s: { x: pct / 100, y: pct / 100, z: pct / 100 } })} className="px-2.5 py-1 rounded-lg border border-slate-600 text-xs text-slate-200 hover:border-sky-500">{pct}%</button>))}
                  <span className="ml-auto text-[10px] font-mono text-slate-500 self-center">acuan: Bambu Studio</span>
                </div>
              </div>
            )}

            {activeReport.length > 0 && <PrintabilityReport checks={activeReport} />}

            {active?.repairNeeded && (() => {
              const sel = repairTierOf(active);
              return (
              <div data-testid="repair-estimate" className="bg-white border border-amber-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-700"><Scissors className="w-4 h-4" /> Estimasi Biaya Perbaikan</div>
                  {sel && <span data-testid="repair-level" className="text-xs font-bold px-2.5 py-1 rounded-full border bg-amber-100 text-amber-700 border-amber-300">{sel.label}</span>}
                </div>
                <p className="text-xs text-slate-500">Kategori awal: <b>QUICK</b> — silakan <b>pilih kategori</b> sesuai tingkat kerusakan menurut Anda.</p>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                  {tiers.map((t) => (
                    <button key={t.key} type="button" data-testid={`repair-tier-${t.key}`} onClick={() => updatePart(active.id, { repairTierKey: t.key })} className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${sel?.key === t.key ? "bg-amber-50 font-semibold text-amber-800" : "text-slate-600 hover:bg-slate-50"}`}>
                      <span className="flex items-center gap-2">{sel?.key === t.key && <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />}{t.label}<span className="text-[11px] text-slate-400 font-normal">· {t.hours} jam</span></span>
                      <span className="font-mono">{formatIDR(t.price)}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-500">Jumlah orang mengerjakan</span>
                  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs">
                    {[1, 2, 3].map((n) => (
                      <button key={n} type="button" data-testid={`repair-people-${n}`} onClick={() => updatePart(active.id, { repairPeople: n })} className={`px-2.5 py-1 transition-colors ${repairPeopleOf(active) === n ? "bg-amber-500 text-white" : "bg-white text-slate-600"}`}>{n}</button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400" data-testid="repair-hours-info">Waktu efektif: <b>{repairEffHoursOf(active, sel)} jam</b> (dibagi {repairPeopleOf(active)} orang, min 3 jam) — masuk hitungan Print Time.</p>
                <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-2"><span>Biaya Repair (indikasi){repairPeopleOf(active) > 1 ? ` × ${repairPeopleOf(active)} orang` : ""}{rush ? " · Express ×2" : ""}</span><span className="font-semibold text-amber-700" data-testid="repair-cost">{formatIDR((sel?.price || 0) * repairPeopleOf(active) * (rush ? 2 : 1))}</span></div>
                <p className="text-[10px] text-slate-400 leading-relaxed" data-testid="repair-disclaimer">Tidak berlaku untuk bentuk bangunan/arsitektur, detailing miniatur dan figure. Harga tidak mengikat atau akan dicek sama tim enginnering kami — ini hanya indikasi awal sebelum tim cek secara detail.</p>
              </div>
              );
            })()}
          </div>

          {/* Config */}
          <div className="space-y-4">
            <label data-testid="upload-input" className="block border-2 border-dashed border-slate-300 rounded-2xl p-5 text-center cursor-pointer hover:border-sky-400 bg-white transition-colors">
              <input type="file" multiple accept=".stl,.obj,.3mf,.step,.stp" className="hidden" onChange={handleFile} />
              <UploadCloud className="w-7 h-7 mx-auto text-slate-400" />
              <p className="text-sm font-medium mt-2">{parts.length ? "Tambah file lain" : "Upload file 3D (STL / OBJ / 3MF)"}</p>
              <p className="text-xs text-slate-500 mt-1">Max 100 MB · bisa pilih beberapa file sekaligus</p>
            </label>

            {parts.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5">
                <div className="text-xs font-mono text-slate-500 mb-1">FILES ({parts.length}) · klik untuk atur</div>
                {reports.map(({ part: p, checks }) => (
                  <div key={p.id} data-testid={`part-${p.id}`} onClick={() => selectPart(p.id)} className={`flex items-center gap-2 text-sm px-2.5 py-2 rounded-lg cursor-pointer ${activeId === p.id ? "bg-sky-50 border border-sky-200" : "hover:bg-slate-50 border border-transparent"}`}>
                    {checks.some((c) => c.level === "error") ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                    <span className="truncate flex-1">{p.name}</span>
                    <span className="text-[11px] font-mono text-slate-400">{p.quantity}× · {(p.s.x * 100) | 0}%</span>
                    <button data-testid={`remove-part-${p.id}`} onClick={(e) => { e.stopPropagation(); removePart(p.id); }} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <label data-testid="add-file-btn" className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-sky-300 rounded-xl py-3 cursor-pointer hover:border-sky-500 hover:bg-sky-50 text-sky-600 font-medium text-sm transition-colors">
                  <input type="file" multiple accept=".stl,.obj,.3mf,.step,.stp" className="hidden" onChange={handleFile} />
                  <Plus className="w-5 h-5" /> Tambah File (bisa pilih beberapa sekaligus)
                </label>
                {parts.length > 1 && <label data-testid="combine-run" className="mt-2 flex items-start gap-2 text-xs text-slate-600 cursor-pointer"><input type="checkbox" data-testid="combine-run-toggle" checked={combine} onChange={(e) => setCombine(e.target.checked)} className="mt-0.5" />
                  <span><b>Gabung dalam 1 kali jalan mesin</b> — file dengan proses &amp; material sama dihitung dari total gram, harga minimum dikenakan sekali.</span></label>}
              </div>
            )}

            {active && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5" data-testid="part-settings">
                <div className="text-xs font-mono text-slate-500 flex items-center gap-2"><span className="truncate">SETTING: {active.name}</span></div>
                <div>
                  <div className="text-xs font-mono text-slate-500 mb-2">PROCESS</div>
                  <div className="grid grid-cols-2 gap-2">
                    {["FDM", "SLA"].map((p) => (<button key={p} data-testid={`process-${p}`} onClick={() => updatePart(active.id, { process: p })} className={`py-2 rounded-lg text-sm font-medium border ${active.process === p ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{p === "FDM" ? "FDM / Filament" : "SLA / Resin"}</button>))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono text-slate-500 mb-2">MATERIAL</div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {activeMats.map((m) => (<button key={m.id} data-testid={`material-${m.name}`} onClick={() => updatePart(active.id, { materialId: m.id })} className={`shrink-0 text-left px-3 py-2 rounded-lg border min-w-[120px] ${active.materialId === m.id ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}><div className="text-sm font-medium">{m.name}</div><div className="text-[11px] text-slate-500 font-mono">{formatIDR(m.price_per_gram)}/g</div></button>))}
                  </div>
                  {(() => { const sm = materials.find((m) => m.id === active.materialId); return sm?.description ? <p className="text-[11px] text-slate-600 mt-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-relaxed" data-testid="material-desc">{sm.description}</p> : null; })()}
                </div>
                {!active.multicolor && (
                  <div data-testid="color-block">
                    <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-2">
                      <span className="flex items-center gap-1.5"><Palette className="w-4 h-4 text-sky-500" /> WARNA {active.process === "SLA" ? "RESIN" : "FILAMENT"}</span>
                      <span className="text-slate-700 font-semibold">{active.color?.name || "Putih"}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(active.process === "SLA" ? SLA_COLORS : FDM_COLORS).map((c) => (
                        <button key={c.name} data-testid={`color-${c.name}`} title={c.name} onClick={() => updatePart(active.id, { color: c })}
                          className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${active.color?.name === c.name ? "border-sky-500 ring-2 ring-sky-200" : "border-slate-300"}`}
                          style={{ background: c.hex }} />
                      ))}
                    </div>
                    {active.process === "FDM" && (
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-xs text-slate-500">Custom:</span>
                        <input data-testid="color-custom-hex" type="color" value={/^#[0-9a-fA-F]{6}$/.test(active.color?.hex || "") ? active.color.hex : "#FFFFFF"} onChange={(e) => updatePart(active.id, { color: { name: `Custom ${e.target.value}`, hex: e.target.value } })} className="w-8 h-8 rounded border border-slate-300 bg-white cursor-pointer" />
                        <input data-testid="color-custom-name" type="text" placeholder="Nama warna (mis. Tosca)" value={(active.color?.name || "").startsWith("Custom") ? "" : (FDM_COLORS.some((c) => c.name === active.color?.name) || SLA_COLORS.some((c) => c.name === active.color?.name) ? "" : active.color?.name || "")} onChange={(e) => updatePart(active.id, { color: { name: e.target.value || `Custom ${active.color?.hex}`, hex: active.color?.hex || "#FFFFFF" } })} className="flex-1 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400 mt-2">Default Putih. Warna tampil di preview, PDF quotation & email.</p>
                  </div>
                )}
                {active.process === "FDM" && (
                  <div>
                    <div className="flex justify-between text-xs font-mono text-slate-500 mb-2"><span>INFILL DENSITY</span><span className="text-sky-600">{active.infill}%</span></div>
                    <input data-testid="infill-slider" type="range" min="10" max="100" step="5" value={active.infill} onChange={(e) => updatePart(active.id, { infill: +e.target.value })} className="w-full accent-sky-500" />
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span>Support structure</span>
                  <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium" data-testid="support-always-on"><CheckCircle2 className="w-4 h-4" /> Always ON</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Unit (file ini)</span>
                  <div className="flex items-center gap-2">
                    <button data-testid="qty-minus" onClick={() => updatePart(active.id, { quantity: Math.max(1, active.quantity - 1) })} className="w-8 h-8 rounded-lg border border-slate-300">−</button>
                    <input data-testid="qty-value" type="number" min="1" value={active.quantity}
                      onChange={(e) => updatePart(active.id, { quantity: Math.max(1, Math.floor(+e.target.value || 1)) })}
                      className="w-14 h-8 text-center font-medium border border-slate-300 rounded-lg focus:outline-none focus:border-sky-500" />
                    <button data-testid="qty-plus" onClick={() => updatePart(active.id, { quantity: active.quantity + 1 })} className="w-8 h-8 rounded-lg border border-slate-300">+</button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between">
              <div><div className="text-sm flex items-center gap-1.5"><Zap className="w-4 h-4 text-amber-500" /> Express (satu order)</div><div className="text-[11px] text-slate-400">Mengikuti jam berjalan setelah payment · +{formatIDR(expCfg.min)} atau +{expCfg.pct}% dari total (terbesar)</div></div>
              <button data-testid="rush-toggle" onClick={() => setRush(!rush)} className={`w-11 h-6 rounded-full transition-colors relative ${rush ? "bg-amber-500" : "bg-slate-300"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${rush ? "translate-x-5" : "left-0.5"}`} /></button>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl p-5">
              <div className="text-xs font-mono text-slate-400">ESTIMATED QUOTE {rush && <span className="text-amber-400">· EXPRESS</span>}</div>
              <div data-testid="quote-total" className="font-head text-3xl font-bold mt-1 flex items-center gap-2">{calcLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : formatIDR(grandTotal)}</div>
              {quote && (
                <div className="mt-4 space-y-1.5 text-sm text-slate-300 font-mono">
                  {(quote.lines || []).map((l, i) => (
                    <div key={i} className="flex justify-between gap-2"><span className="truncate">{l.name} ×{l.quantity}{l.minimum_applied && <span className="text-amber-400 text-[10px]"> · harga min.</span>}{l.combined && <span className="text-sky-300 text-[10px]" data-testid={`quote-combined-${i}`}> · {l.combined}</span>}{l.discount_percent > 0 && <span className="text-emerald-400 text-[10px]"> -{l.discount_percent}%</span>}</span><span className="text-right shrink-0">{formatIDR(l.line_total)}</span></div>
                  ))}
                  <div className="border-t border-slate-700 pt-1.5 mt-1.5 grid grid-cols-2 gap-y-1">
                    <span>Weight</span><span className="text-right">{quote.estimated_weight_g} g</span>
                    {quote.discount > 0 && (<><span>Discount</span><span className="text-right text-emerald-400" data-testid="quote-discount">-{formatIDR(quote.discount)}</span></>)}
                    {rush && quote.express_surcharge > 0 && (<><span>Express</span><span className="text-right text-amber-400">+{formatIDR(quote.express_surcharge)}</span></>)}
                    {repairTotal > 0 && (<><span>Biaya Repair{rush ? " (Express ×2)" : ""}</span><span className="text-right text-amber-400" data-testid="quote-repair-line">+{formatIDR(repairTotal)}</span></>)}
                    {member && addCostTotal > 0 && (<><span>Biaya Tambahan</span><span className="text-right text-sky-400" data-testid="quote-addcost-line">+{formatIDR(addCostTotal)}</span></>)}
                    {taxAmt > 0 && (<><span>{taxLabel(taxDef)}</span><span className="text-right" data-testid="quote-tax-line">{taxDef?.mode === "inclusive" ? "" : "+"}{formatIDR(taxAmt)}</span></>)}
                    {(() => { const expHpd = +designCfg?.repair_express_hours_per_day || 12; const regHpd = +designCfg?.repair_reguler_hours_per_day || 8; const repD = repairHoursEff > 0 ? (rush ? Math.floor(repairHoursEff / expHpd) : Math.max(1, Math.ceil(repairHoursEff / regHpd))) : 0; const repH = rush ? Math.round((repairHoursEff - repD * expHpd) * 10) / 10 : 0; const pureDays = rush ? Math.max(1, (quote.express_work_days ?? 1) - repD) : Math.max(1, (quote.work_days ?? 1) - repD); const totDays = pureDays + repD; return (<>
                      {rush && quote.express_time_hours != null ? (
                        <><span className="text-amber-300">Print Time Express</span><span className="text-right text-amber-300" data-testid="quote-workdays">{pureDays} hari waktu berjalan <span className="text-slate-500">({quote.express_time_hours} h{quote.machines_available ? ` ÷ ${quote.machines_available} mesin` : ""} · +{quote.express_buffer_h}h buffer)</span><br/><span className="text-slate-500">Sabtu–Minggu tetap terhitung</span></span></>
                      ) : (
                        <><span>Print Time Reguler</span><span className="text-right" data-testid="quote-workdays">{pureDays} hari kerja <span className="text-slate-500">({quote.print_time_hours} h{quote.machines_free ? ` ÷ ${quote.machines_free} mesin` : ""})</span><br/><span className="text-slate-500">Sabtu–Minggu libur</span></span></>
                      )}
                      {repairHoursEff > 0 && (<><span className="text-amber-300/80">+ Waktu Design/Repair</span><span className="text-right text-amber-300/80" data-testid="quote-repair-days">{rush ? `${repD > 0 ? `${repD} hari${repH > 0 ? ` ${repH} jam` : ""}` : `${repH} jam`} waktu berjalan (${expHpd} jam/hari)` : `${repD} hari kerja (${regHpd} jam/hari)`} <span className="text-slate-500">· {repairHoursEff.toFixed(1)} jam efektif</span></span></>)}
                      <span className="font-bold text-white border-t border-slate-600 pt-1.5 mt-1">TOTAL PRINT TIME</span><span className="text-right font-bold text-white border-t border-slate-600 pt-1.5 mt-1" data-testid="quote-total-time">{rush ? `${totDays} hari${repH > 0 ? ` ${repH} jam` : ""} waktu berjalan` : `${totDays} hari kerja`}<br/><span className="text-slate-400 font-normal">terhitung sejak payment masuk</span></span>
                    </>); })()}
                    {quote.minimum_applied && <span className="col-span-2 text-amber-400 text-[11px]" data-testid="minimum-applied-note">Harga minimum mesin jalan diterapkan (per material)</span>}
                  </div>
                </div>
              )}
              {repairTotal > 0 && <div className="mt-3 text-[11px] text-amber-300 flex items-start gap-1.5"><Scissors className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Termasuk estimasi biaya repair otomatis {formatIDR(repairTotal)} — dapat disesuaikan tim appraisal.</div>}
              <button data-testid="get-quote-btn" disabled={!quote} onClick={() => (embedded ? saveQuoteAndEdit(true) : setShowSummary(true))} className="w-full mt-4 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 rounded-xl py-3 font-medium transition-colors">{embedded ? "Get Quotation" : "Get Final Quote"}</button>
            </div>
          </div>
        </div>
      </div>

      {showSummary && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowSummary(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-xl font-semibold">Quotation Summary</h3>
            <p className="text-sm text-slate-500 mb-4">Lengkapi data Anda. Saat dikirim, email ke {contacts.email} & WhatsApp otomatis terbuka dengan No. ID yang sama, dan PDF ter-download.</p>
            {repairTotal > 0 && <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs mb-3" data-testid="repair-note">Sudah termasuk estimasi biaya repair otomatis sebesar <b>{formatIDR(repairTotal)}</b>. Tidak berlaku untuk bentuk bangunan/arsitektur, detailing miniatur dan figure. Harga tidak mengikat atau akan dicek sama tim enginnering kami — ini hanya indikasi awal sebelum tim cek secara detail.</div>}
            <div className="space-y-3">
              {member && (
                <div className="relative" data-testid="customer-lookup">
                  <input data-testid="customer-search" placeholder="Cari customer terdaftar (nama / WA / email)…" value={custQuery}
                    onChange={(e) => { setCustQuery(e.target.value); setShowCustDrop(true); }}
                    onFocus={() => setShowCustDrop(true)}
                    className="w-full border border-sky-300 bg-sky-50 rounded-lg px-3 py-2 text-sm" />
                  {showCustDrop && custMatches.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                      {custMatches.map((c) => (
                        <button key={c.id} data-testid={`customer-opt-${c.id}`} onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0">
                          <div className="font-medium">{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                          <div className="text-[11px] text-slate-500">{c.whatsapp || "-"} · {c.email || "-"}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <input data-testid="qf-customer_name" placeholder="Nama *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {["Perusahaan", "Pribadi"].map((t) => (
                    <button key={t} data-testid={`entity-${t}`} onClick={() => setForm({ ...form, entity_type: t })} className={`py-2 rounded-lg text-sm font-medium border ${form.entity_type === t ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{t}</button>
                  ))}
                </div>
                {form.entity_type === "Perusahaan" && (
                  <input data-testid="qf-company" placeholder="Nama Perusahaan" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                )}
              </div>
              <input data-testid="qf-whatsapp" placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="qf-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="qf-project_name" placeholder="Nama Project" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <textarea data-testid="qf-notes" placeholder="Catatan" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} />
            </div>
            <div className="bg-slate-900 text-white rounded-lg p-3 mt-4 text-sm font-mono flex justify-between" data-testid="grand-total-row"><span>GRAND TOTAL{taxAmt > 0 ? ` (termasuk ${taxDef?.name || "pajak"})` : ""}</span><span className="font-semibold">{formatIDR(grandTotal)}</span></div>

            {member && (
              <div className="mt-4 border border-sky-200 bg-sky-50 rounded-xl p-3 flex items-center justify-between" data-testid="member-edit-panel">
                <div className="text-xs font-semibold text-sky-700 inline-flex items-center gap-1.5"><UserCog className="w-3.5 h-3.5" /> Edit oleh {member.name || member.email} ({member.role})</div>
                <button data-testid="member-logout-btn" onClick={memberLogout} className="text-[11px] text-slate-500 hover:text-rose-500 inline-flex items-center gap-1"><LogOut className="w-3 h-3" /> Keluar</button>
              </div>
            )}

            {member && (
              <div className="flex gap-2 mt-4">
                <button data-testid="preview-pdf-btn" onClick={previewPdf} disabled={pdfBusy || !quote} className="flex-1 border border-slate-300 text-slate-700 rounded-xl py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-slate-50 disabled:opacity-40">
                  {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
                </button>
                <button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={pdfBusy || !quote} title="Download PDF quotation (Member FOMU)" className="border border-sky-300 text-sky-700 bg-sky-50 rounded-xl px-3.5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-sky-100 disabled:opacity-40">
                  <Download className="w-4 h-4" /> Download
                </button>
              </div>
            )}
            <button data-testid="save-edit-quote-btn" onClick={() => saveQuoteAndEdit(false)} disabled={!quote} className="w-full mt-2 border border-slate-900 text-slate-900 rounded-xl py-2.5 font-medium inline-flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-40"><Pencil className="w-4 h-4" /> Simpan & Edit (buka di Quotation)</button>
            <button data-testid="submit-quote-btn" onClick={submitQuote} className="w-full mt-2 bg-slate-900 text-white rounded-xl py-3 font-medium inline-flex items-center justify-center gap-2 hover:bg-slate-800"><Send className="w-4 h-4" /> Kirim Quotation (Email + WhatsApp)</button>
            <p className="text-[11px] text-slate-400 text-center mt-2 inline-flex items-center justify-center gap-1 w-full flex-wrap" data-testid="member-footer">
              <Pencil className="w-3 h-3" /> penambahan /edit quotation{" "}
              {member
                ? <><span className="text-sky-600 font-medium">MEMBER FOMU</span><span className="text-slate-300">·</span><button data-testid="member-footer-logout" onClick={memberLogout} className="text-rose-500 underline underline-offset-2 hover:text-rose-600">Keluar</button></>
                : <button data-testid="member-fomu-link" onClick={() => setShowMemberLogin(true)} className="text-sky-600 font-medium underline underline-offset-2 hover:text-sky-700">MEMBER FOMU</button>}
            </p>
          </div>
        </div>
      )}

      {showMemberLogin && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowMemberLogin(false)} data-testid="member-login-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-lg font-semibold inline-flex items-center gap-2"><UserCog className="w-5 h-5 text-sky-600" /> Login Member FOMU</h3>
            <p className="text-sm text-slate-500 mb-4 mt-1">Hanya member berwenang ({memberRoles.join(" / ")}) yang dapat mengedit quotation.</p>
            <div className="space-y-3">
              <input data-testid="member-email" type="email" placeholder="Email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="member-password" type="password" placeholder="Password" value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && memberLogin()} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button data-testid="member-login-btn" disabled={memberBusy || !memberForm.email || !memberForm.password} onClick={memberLogin} className="w-full mt-4 bg-sky-600 text-white rounded-xl py-2.5 font-medium inline-flex items-center justify-center gap-2 hover:bg-sky-500 disabled:opacity-40">
              {memberBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Masuk
            </button>
          </div>
        </div>
      )}
      {!embedded && <PublicFooter />}
    </div>
  );
}
